import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImagePlus, Loader2, RotateCcw, Save, Trash2 } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { RootState } from '@/app/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  createAdminHomepageSliderImage,
  deleteAdminHomepageSliderImage,
  fetchAdminHomepageSliderImages,
  updateAdminHomepageSliderImage,
  type AdminHomepageSliderImage,
} from '@/services/adminService';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';

type DraftState = {
  altText: string;
  title: string;
  caption: string;
  linkUrl: string;
  sortOrder: string;
  isActive: boolean;
};

const toDraft = (image: AdminHomepageSliderImage): DraftState => ({
  altText: image.altText,
  title: image.title ?? '',
  caption: image.caption ?? '',
  linkUrl: image.linkUrl ?? '',
  sortOrder: String(image.sortOrder),
  isActive: image.isActive,
});

const formatBytes = (value: number | null | undefined) => {
  if (!value || value <= 0) {
    return '0 KB';
  }
  if (value < 1024 * 1024) {
    return `${Math.round(value / 1024)} KB`;
  }
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
};

const AdminHomepageSliderPage = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const { toast } = useToast();

  const [images, setImages] = useState<ReadonlyArray<AdminHomepageSliderImage>>([]);
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newAltText, setNewAltText] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newSortOrder, setNewSortOrder] = useState('0');
  const [newIsActive, setNewIsActive] = useState(true);

  const loadImages = useCallback(async () => {
    try {
      setStatus('loading');
      const result = await fetchAdminHomepageSliderImages();
      setImages(result);
      setDrafts(Object.fromEntries(result.map((image) => [image.id, toDraft(image)])));
      setStatus('idle');
    } catch (error) {
      logging.error('Failed to load homepage slider images', { error });
      setStatus('error');
      toast({
        title: 'Failed to load slider images',
        description: error instanceof Error ? error.message : 'Could not load homepage slider images.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/', { replace: true });
      return;
    }
    void loadImages();
  }, [isAdmin, loadImages, navigate]);

  if (!isAdmin) {
    return null;
  }

  const updateDraft = (id: string, patch: Partial<DraftState>) => {
    setDrafts((current) => ({
      ...current,
      [id]: {
        ...current[id],
        ...patch,
      },
    }));
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewFile(event.target.files?.[0] ?? null);
  };

  const handleUpload = async () => {
    if (!newFile) {
      toast({ title: 'Select an image first', variant: 'destructive' });
      return;
    }
    const altText = newAltText.trim() || newTitle.trim() || newFile.name;

    try {
      analytics.track('button: ui.admin_homepage_slider_upload');
      setUploading(true);
      await createAdminHomepageSliderImage({
        file: newFile,
        altText,
        title: newTitle,
        caption: newCaption,
        linkUrl: newLinkUrl,
        sortOrder: Number(newSortOrder) || 0,
        isActive: newIsActive,
      });
      setNewFile(null);
      setNewAltText('');
      setNewTitle('');
      setNewCaption('');
      setNewLinkUrl('');
      setNewSortOrder('0');
      setNewIsActive(true);
      toast({ title: 'Slider image uploaded' });
      await loadImages();
    } catch (error) {
      logging.error('Failed to upload homepage slider image', { error });
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Could not upload slider image.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (image: AdminHomepageSliderImage) => {
    const draft = drafts[image.id];
    if (!draft) {
      return;
    }

    try {
      analytics.track('button: ui.admin_homepage_slider_save', { imageId: image.id });
      setSavingId(image.id);
      await updateAdminHomepageSliderImage(image.id, {
        altText: draft.altText,
        title: draft.title,
        caption: draft.caption,
        linkUrl: draft.linkUrl,
        sortOrder: Number(draft.sortOrder) || 0,
        isActive: draft.isActive,
      });
      toast({ title: 'Slider image saved' });
      await loadImages();
    } catch (error) {
      logging.error('Failed to update homepage slider image', { error, imageId: image.id });
      toast({
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Could not save slider image.',
        variant: 'destructive',
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (image: AdminHomepageSliderImage) => {
    if (!window.confirm(`Delete ${image.originalFilename}?`)) {
      return;
    }

    try {
      analytics.track('button: ui.admin_homepage_slider_delete', { imageId: image.id });
      setDeletingId(image.id);
      await deleteAdminHomepageSliderImage(image.id);
      toast({ title: 'Slider image deleted' });
      await loadImages();
    } catch (error) {
      logging.error('Failed to delete homepage slider image', { error, imageId: image.id });
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Could not delete slider image.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pb-12 pt-8">
      <div className="grid gap-6">
        <Card>
          <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-2xl">Homepage slider</CardTitle>
              <CardDescription>Upload and order optimized images shown on the public home page.</CardDescription>
            </div>
            <Button onClick={() => void loadImages()} disabled={status === 'loading'} variant="outline">
              {status === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
              Reload
            </Button>
          </CardHeader>
          <CardContent className="grid items-start gap-4 md:grid-cols-[1.1fr_0.9fr]">
            <div className="grid content-start gap-2">
              <Label htmlFor="slider-image">Image</Label>
              <Input id="slider-image" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} />
              <p className="text-xs text-muted-foreground">JPG, PNG, or WebP. Backend stores desktop and thumbnail WebP versions only.</p>
            </div>
            <div className="grid content-start gap-2">
              <Label htmlFor="slider-alt">Alt text</Label>
              <Input id="slider-alt" value={newAltText} onChange={(event) => setNewAltText(event.target.value)} />
            </div>
            <div className="grid content-start gap-2">
              <Label htmlFor="slider-title">Title</Label>
              <Input id="slider-title" value={newTitle} onChange={(event) => setNewTitle(event.target.value)} />
            </div>
            <div className="grid content-start gap-2">
              <Label htmlFor="slider-link">Link URL</Label>
              <Input id="slider-link" value={newLinkUrl} onChange={(event) => setNewLinkUrl(event.target.value)} />
            </div>
            <div className="grid content-start gap-2 md:col-span-2">
              <Label htmlFor="slider-caption">Caption</Label>
              <Textarea id="slider-caption" value={newCaption} onChange={(event) => setNewCaption(event.target.value)} rows={2} />
            </div>
            <div className="grid content-start gap-2">
              <Label htmlFor="slider-order">Sort order</Label>
              <Input id="slider-order" type="number" value={newSortOrder} onChange={(event) => setNewSortOrder(event.target.value)} />
            </div>
            <div className="flex items-center gap-3 self-end">
              <Switch checked={newIsActive} onCheckedChange={setNewIsActive} id="slider-active" />
              <Label htmlFor="slider-active">Active</Label>
            </div>
            <div className="md:col-span-2">
              <Button onClick={handleUpload} disabled={uploading || !newFile}>
                {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}
                Upload slider image
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {images.length === 0 && status !== 'loading' && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">No homepage slider images uploaded yet.</CardContent>
            </Card>
          )}
          {images.map((image) => {
            const draft = drafts[image.id] ?? toDraft(image);
            const isSaving = savingId === image.id;
            const isDeleting = deletingId === image.id;
            return (
              <Card key={image.id}>
                <CardContent className="grid gap-5 p-4 lg:grid-cols-[260px_1fr_auto]">
                  <div className="overflow-hidden rounded-md border bg-muted">
                    <img
                      src={image.thumbnailUrl ?? image.imageUrl}
                      alt={image.altText}
                      className="aspect-video h-full w-full object-cover"
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor={`alt-${image.id}`}>Alt text</Label>
                      <Input id={`alt-${image.id}`} value={draft.altText} onChange={(event) => updateDraft(image.id, { altText: event.target.value })} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor={`title-${image.id}`}>Title</Label>
                      <Input id={`title-${image.id}`} value={draft.title} onChange={(event) => updateDraft(image.id, { title: event.target.value })} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor={`link-${image.id}`}>Link URL</Label>
                      <Input id={`link-${image.id}`} value={draft.linkUrl} onChange={(event) => updateDraft(image.id, { linkUrl: event.target.value })} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor={`order-${image.id}`}>Sort order</Label>
                      <Input id={`order-${image.id}`} type="number" value={draft.sortOrder} onChange={(event) => updateDraft(image.id, { sortOrder: event.target.value })} />
                    </div>
                    <div className="grid gap-2 md:col-span-2">
                      <Label htmlFor={`caption-${image.id}`}>Caption</Label>
                      <Textarea id={`caption-${image.id}`} value={draft.caption} onChange={(event) => updateDraft(image.id, { caption: event.target.value })} rows={2} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground md:col-span-2">
                      <span>{image.originalFilename}</span>
                      <span>Original {formatBytes(image.originalSizeBytes)}</span>
                      <span>Desktop {formatBytes(image.desktopSizeBytes)}</span>
                      <span>Thumb {formatBytes(image.thumbnailSizeBytes)}</span>
                      {image.widthPx && image.heightPx ? <span>{image.widthPx} x {image.heightPx}px</span> : null}
                    </div>
                  </div>
                  <div className="flex flex-row items-center gap-2 lg:flex-col lg:items-stretch">
                    <div className="flex items-center gap-2 lg:justify-end">
                      <Switch checked={draft.isActive} onCheckedChange={(checked) => updateDraft(image.id, { isActive: checked })} id={`active-${image.id}`} />
                      <Label htmlFor={`active-${image.id}`}>Active</Label>
                    </div>
                    <Button onClick={() => void handleSave(image)} disabled={isSaving || isDeleting}>
                      {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                      Save
                    </Button>
                    <Button variant="destructive" onClick={() => void handleDelete(image)} disabled={isSaving || isDeleting}>
                      {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminHomepageSliderPage;
